import { useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import { INTRODUCTION_SECTION_ID, LanguageCodes } from 'global/constants';
import { useGetActivitySections } from 'approot/activity-builder/activity-builder-section.hooks';
import { BaseSectionOverview } from 'approot/activity-builder/activity-builder.constants';
import { getSectionsAndSubSections } from 'approot/activity-builder/activity-builder.utils';
import ActivityAnswersPrintGenerator from './activity-answers-print-generator';
import { ActivityPrintMetadata } from './activity-answers-print.constants';
import { PRESENT_PRINT_CONTAINER } from 'approot/activity-builder/present/activity-builder-present.constants';

type Props = {
  contentId: number;
  contentFontSize?: number;
  classId: number;
  version: string;
  studentId?: number;
  language?: LanguageCodes;
  metadata: ActivityPrintMetadata;
  sectionOverviews: BaseSectionOverview[];
  selectedSectionIds: string[];
  onRenderComplete: () => void;
};

const ActivityAnswersPrint = ({
  contentId,
  contentFontSize = 16,
  classId,
  version,
  studentId,
  language,
  metadata,
  sectionOverviews,
  selectedSectionIds,
  onRenderComplete,
}: Props) => {
  const completedCount = useRef<number>(0);

  // fetch the sections to print
  const { data: sectionsData } = useGetActivitySections({
    contentId,
    contentType: metadata.contentType,
    classId,
    sectionIds: selectedSectionIds,
    language,
    version,
  });

  const sections = useMemo(() => {
    return getSectionsAndSubSections(
      sectionsData?.getActivitiesSections || []
    ).filter(s => selectedSectionIds.includes(s.id));
  }, [selectedSectionIds, sectionsData]);

  // All children must render and size first
  const handleChildCallback = (totalPages: number) => {
    completedCount.current += 1;
    if (completedCount.current === totalPages) {
      onRenderComplete();
    }
  };

  const el = document.getElementById(PRESENT_PRINT_CONTAINER);

  return el && sections.length > 0
    ? createPortal(
        <ActivityAnswersPrintGenerator
          contentFontSize={contentFontSize}
          metadata={metadata}
          language={language}
          sections={sections}
          sectionOverviews={sectionOverviews}
          studentId={studentId}
          includesIntro={selectedSectionIds.includes(INTRODUCTION_SECTION_ID)}
          onRenderComplete={handleChildCallback}
          contentVersion={version}
        />,
        el
      )
    : null;
};

export default ActivityAnswersPrint;
